import {
  LightweightElement,
  LightweightElementsRecord,
} from "@/store/lightweight-elements-slice";
import { assert, walkWithQueue } from "@faro-lotv/foundation";
import {
  GENERIC_DATASET_TYPE_HINTS,
  GUID,
  IElement,
  IElementType,
  IElementTypeHint,
} from "@faro-lotv/ielement-types";
import {
  ClustersRecord,
  IElementsRecord,
  newestToOldest,
} from "@faro-lotv/project-source";
import { partition } from "es-toolkit";
import { CustomDatesCache } from "./custom-date-utils";
import { createArea4dSessionNodeIfNeeded } from "./generate-cluster-tree";
import { generateUnfilteredTree } from "./generate-unfiltered-tree";
import { elementToTreeData, TreeData } from "./tree-data";
import { isElementNodeToHide, shouldHideIfEmpty } from "./tree-discriminators";
import { AreaDataSets, TreeGenerationData } from "./tree-filters-utils";
import { sheetsFilter } from "./tree-render-filters";

type GenerateProjectTreeParams = {
  /** ID of the parent of the current recursion level */
  parentId: GUID;

  /** All IElements of the project */
  elements: LightweightElementsRecord | IElementsRecord;

  /** The capture-tree datasets contained in each area @default {} */
  areaDataSets?: AreaDataSets;

  /** The main revision tree used to build the clusters structure @default {} */
  clusters?: ClustersRecord;

  /** true to return the un-filtered version of the project @default false */
  fullTree?: boolean;

  /** Id of the tag identifying rooms copied for video mode datasets */
  videoModeCopyTagId?: GUID;
};

/**
 * @returns The tree representation of the whole project
 */
export function generateProjectTree({
  parentId,
  elements,
  areaDataSets = {},
  clusters = {},
  fullTree = false,
  videoModeCopyTagId,
}: GenerateProjectTreeParams): TreeData[] {
  if (fullTree) return generateUnfilteredTree(parentId, elements);

  const customDatesCache = new CustomDatesCache();

  const tree = generateFilteredTree(
    parentId,
    elements,
    {
      areaDataSets,
      videoModeCopyTagId,
      clusters,
    },
    customDatesCache,
  );

  return tree;
}

/**
 * Generates the project tree with date, text, and tags filtering applied recursively
 *
 * @param parentId ID of the parent of the current recursion level
 * @param elements All IElements of the project
 * @param treeGenerationData Data that stay constant during the tree generation
 * @param customDatesCache Cache for custom dates to avoid redundant calculations
 * @returns The filtered tree representation
 */
function generateFilteredTree(
  parentId: GUID,
  elements: IElementsRecord | LightweightElementsRecord,
  treeGenerationData: TreeGenerationData,
  customDatesCache: CustomDatesCache,
): TreeData[] {
  const subtree: TreeData[] = [];

  const parentElement = elements[parentId];
  if (!parentElement) return subtree;

  // The elements of the current level of recursion.
  // Add datasets from the capture tree as children of the area
  // if they are exposing the clusters iElements.
  const childrenIds = [...(parentElement.childrenIds ?? [])];

  for (const { elementId } of treeGenerationData.areaDataSets[
    parentElement.id
  ] ?? []) {
    const dataset = elements[elementId];
    if (
      dataset?.childrenIds?.some(
        (id) => elements[id]?.type === IElementType.cluster,
      )
    ) {
      childrenIds.push(dataset.id);
      // Calculate and cache the custom date for the dataset
      customDatesCache.findCustomDateForDataset(dataset.id, elements);
    }
  }

  subtree.push(
    ...createArea4dSessionNodeIfNeeded(
      parentElement,
      elements,
      treeGenerationData,
    ),
  );

  for (const elementId of childrenIds) {
    const element = elements[elementId];
    if (
      !element ||
      isElementNodeToHide(
        element,
        elements,
        treeGenerationData.videoModeCopyTagId,
      )
    ) {
      continue;
    }

    subtree.push(
      ...processChildren(
        element,
        elements,
        parentElement,
        treeGenerationData,
        customDatesCache,
      ),
    );
  }
  return subtree;
}

/**
 * This array is created to quickly check whether an element's type hint is among
 * the dataset type hints. Array methods like .find or .includes do not work on
 * GENERIC_DATASET_TYPE_HINTS because the type of element of the latter array is
 * not string | null | undefined.
 */
const datasetTypeHints = Object.values<string | null | undefined>(
  GENERIC_DATASET_TYPE_HINTS,
);

function processChildren(
  element: LightweightElement | IElement,
  elements: LightweightElementsRecord | IElementsRecord,
  parentElement: LightweightElement | IElement,
  treeGenerationData: TreeGenerationData,
  customDatesCache: CustomDatesCache,
): TreeData[] {
  const customDate = customDatesCache.customDates.get(element.id);

  // Skip videowalk dataset that did not process properly
  if (
    element.type === IElementType.section &&
    element.typeHint === IElementTypeHint.dataSession
  ) {
    const stack = [...(element.childrenIds ?? [])];
    while (stack.length > 0) {
      const id = stack.pop();
      if (!id) break;

      const currentElement = elements[id];
      if (!currentElement) continue;

      // A properly processed videomode/holocam dataset always has a `Section|Cluster` child
      if (
        currentElement.type === IElementType.section &&
        currentElement.typeHint === IElementTypeHint.dataSetVideoWalk &&
        !currentElement.childrenIds?.some(
          (childId) =>
            elements[childId]?.type === IElementType.section &&
            elements[childId].typeHint === IElementTypeHint.cluster,
        )
      ) {
        return [];
      }

      stack.push(...(currentElement.childrenIds ?? []));
    }
  }

  const filterResult = sheetsFilter(
    element,
    parentElement.type,
    treeGenerationData.videoModeCopyTagId,
    customDate,
  );

  let children: TreeData[] = [];
  if (filterResult.shouldKeepChildren) {
    children = generateFilteredTree(
      element.id,
      elements,
      treeGenerationData,
      customDatesCache,
    );
  }

  if (
    element.type === IElementType.timeSeries ||
    (element.type === IElementType.section &&
      element.typeHint === IElementTypeHint.room &&
      parentElement.type === IElementType.group)
  ) {
    children.sort((a, b) => {
      assert(
        a.element && b.element,
        "Node children of a timeseries should be linked to an IElement",
      );
      return newestToOldest(a.element, b.element);
    });
  }

  // A group or time series should not be visible if they're empty
  if (shouldHideIfEmpty(element)) {
    filterResult.shouldKeepElement &&= children.length > 0;
  }

  if (filterResult.shouldKeepElement) {
    // Move point clouds to front
    const [pcs, rest] = partition(
      children,
      (node) =>
        node.element?.typeHint === IElementTypeHint.dataSession ||
        datasetTypeHints.includes(node.element?.typeHint),
    );
    children = [...pcs, ...rest];

    // Move location markers to the end
    const [locationMarkers, others] = partition(
      children,
      (node) =>
        node.element?.typeHint === IElementTypeHint.room &&
        node.directParent?.type !== IElementType.timeSeries &&
        !node.children,
    );
    if (locationMarkers.length) {
      children = [...others, ...locationMarkers];
    }

    // Put capture tree datasets in front of everything
    if (
      element.type === IElementType.section &&
      element.typeHint === IElementTypeHint.area
    ) {
      const [datasets, rest] = partition(
        children,
        (node) =>
          !!node.element?.typeHint &&
          Object.values<string>(GENERIC_DATASET_TYPE_HINTS).includes(
            node.element.typeHint,
          ),
      );
      children = [...datasets, ...rest];
    }

    return [
      elementToTreeData({
        element,
        parentElement,
        children,
        createdAt: customDate,
        label: filterResult.displayName ?? element.name,
      }),
    ];
  } else if (filterResult.shouldKeepChildren) {
    return children;
  }

  return [];
}

/**
 * @returns all ids of a tree in a Set for quick access
 * @param tree the tree to collect the ids from
 */
export function createSetOfAllTreeIds(tree: TreeData[]): Set<GUID> {
  const allIds = new Set<GUID>();
  walkWithQueue([...tree], (item, append) => {
    allIds.add(item.id);
    if (item.children) append(...item.children);
  });
  return allIds;
}
